<template>
  <div class="tasks-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ isMine ? t('tasks.myTitle') : t('tasks.title') }}</h1>
        <p class="page-sub">{{ isMine ? t('tasks.mySub') : t('tasks.sub') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-ghost" :disabled="!tasks.length" @click="exportTasks">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {{ t('common.export') }}
        </button>
        <!-- <button v-if="!isExternal" class="btn btn-primary" @click="openCreateForm">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ t('tasks.newTask') }}
        </button> -->
      </div>
    </div>

    <!-- Filters bar -->
    <div class="filters">
      <div class="status-tabs">
        <button
          v-for="s in statusOpts"
          :key="s.value"
          class="tab-btn"
          :class="{ active: filters.status === s.value }"
          @click="filters.status = s.value; load()"
        >{{ s.label }}</button>
      </div>
      <div class="filter-controls">
        <select v-model="filters.urgency" class="form-control filter-sel" @change="load()">
          <option value="">{{ t('tasks.allUrgency') }}</option>
          <option value="critical">{{ t('urgency.critical') }}</option>
          <option value="high">{{ t('urgency.high') }}</option>
          <option value="normal">{{ t('urgency.normal') }}</option>
          <option value="low">{{ t('urgency.low') }}</option>
        </select>
        <div class="date-filter">
          <input v-model="filters.due_from" type="date" class="form-control filter-date" :title="t('common.from')" @change="load()" />
          <span class="date-sep">–</span>
          <input v-model="filters.due_to" type="date" class="form-control filter-date" :title="t('common.to')" @change="load()" />
        </div>
        <input
          v-model="filters.q"
          class="form-control filter-search"
          :placeholder="t('common.search')"
          @keyup.enter="load()"
        />
        <button class="btn btn-ghost btn-sm" @click="resetFilters">{{ t('common.reset') }}</button>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="groupedTasks.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      <p>{{ t('tasks.empty') }}</p>
    </div>

    <!-- Tasks grouped under their project as a table (ungrouped tasks last). -->
    <div v-else class="task-groups">
      <section
        v-for="g in groupedTasks"
        :key="g.key"
        class="proj-table"
        :style="{ '--g-color': g.color || 'var(--border-2)', '--tt-cols': colsFor(g) }"
      >
        <!-- Project box header -->
        <div class="pt-head">
          <span class="pt-dot" :style="{ background: g.color || 'var(--border-2)' }"></span>
          <h3 class="pt-name">{{ g.name }}</h3>
          <span class="pt-count">{{ g.tasks.length }}</span>
          <button v-if="!isExternal && g.key" class="pt-add" @click="openCreateInProject(g.key)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ t('tasks.newTask') }}
          </button>
          <button v-if="!isExternal && g.key" class="pt-add pt-field" @click="openFields(g)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ t('fields.addField') }}
          </button>
          <button
            v-if="!isExternal && g.key"
            class="pt-menu"
            :title="t('projects.editProject')"
            @click="editProject(g.key)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
          </button>
        </div>

        <!-- Header + rows share one horizontal scroller so wide column sets scroll
             instead of crushing the subject column. -->
        <div class="pt-scroll">
        <!-- Column headers — subject pinned first, action pinned last; every middle
             column (time/urgency/assignee + custom fields) is drag-reorderable. -->
        <div class="pt-cols">
          <span class="ptc">{{ t('teamReports.subject') }}</span>
          <span
            v-for="(key, i) in orderedMiddles(g)"
            :key="key"
            class="ptc ptc-field"
            :class="colClass(g, i)"
            draggable="true"
            @dragstart="dragStart(g, i)"
            @dragenter="dragEnter(g, i)"
            @dragover.prevent
            @drop="dropOn(g, i)"
            @dragend="dragEnd"
          >
            <span class="ptc-label">{{ colLabel(g, key) }}</span>
            <button
              v-if="!isBuiltinCol(key)"
              class="ptc-del"
              :title="t('fields.remove')"
              draggable="false"
              @click.stop="deleteField(key)"
              @mousedown.stop
              @dragstart.stop.prevent
            >×</button>
          </span>
          <span class="ptc"></span>
        </div>

        <TransitionGroup name="trow" tag="div" class="pt-rows">
          <TaskCard
            v-for="task in g.tasks"
            :key="task.id"
            :task="task"
            :users="users"
            :fields="g.fields"
            :columns="orderedMiddles(g)"
            table
            @open="openTask"
            @complete="completeTask"
            @reopen="reopenTask"
            @restore="restoreTask"
            @assign="assignTask"
            @timer-update="onTimerUpdate"
            @field-value="onFieldValue"
            @delete="deleteTask"
          />
        </TransitionGroup>
        </div>

        <!-- Inline quick-add at the end of the project's list: a title-only task.
             Enter creates and keeps the field open for rapid entry; Esc/blur cancels. -->
        <div v-if="!isExternal && g.key" class="pt-quickadd">
          <input
            v-if="quickAddPid === g.key"
            v-model="quickAddText"
            class="qa-input"
            :placeholder="t('projects.quickAddPlaceholder')"
            @keyup.enter="submitQuickAdd(g)"
            @keyup.esc="cancelQuickAdd"
            @blur="cancelQuickAdd"
          />
          <button v-else class="qa-btn" @click="openQuickAdd(g)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ t('projects.quickAdd') }}
          </button>
        </div>
      </section>
    </div>

    <!-- Detail/edit side drawer (opened by clicking a task) -->
    <TaskDetailDrawer
      v-if="drawerTask"
      :task="drawerTask"
      :projects="projects"
      @close="drawerTask = null"
      @saved="onDrawerSaved"
      @changed="load(true)"
      @complete="completeTask"
      @reopen="reopenTask"
      @restore="restoreTask"
    />

    <!-- Create-only modal (editing happens in the drawer) -->
    <TaskFormModal
      v-if="showForm"
      :projects="projects"
      :defaults="createDefaults"
      @close="closeForm"
      @saved="onSaved"
    />

    <FieldManagerModal
      v-if="fieldModalProject"
      :project-id="fieldModalProject.id"
      :project-name="fieldModalProject.name"
      :fields="fieldsFor(fieldModalProject.id)"
      @close="fieldModalProject = null"
      @changed="loadFields"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TaskCard      from '@/components/TaskCard.vue'
import TaskFormModal from '@/components/TaskFormModal.vue'
import TaskDetailDrawer from '@/components/TaskDetailDrawer.vue'
import FieldManagerModal from '@/components/FieldManagerModal.vue'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'
import { useToast } from '@/composables/useToast'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { exportCsv, secondsToHours } from '@/utils/exportCsv'
import api from '@/api'

export default {
  name: 'TasksView',
  components: { TaskCard, TaskFormModal, TaskDetailDrawer, FieldManagerModal },
  setup() {
    const route   = useRoute()
    const router  = useRouter()

    // 3-dot on a project header → Projects view with its edit modal pre-opened.
    function editProject(pid) {
      router.push({ name: 'projects', query: { edit: pid } })
    }
    const { t }   = useI18n()
    const toast   = useToast()
    const { isExternalClient } = useAuth()
    const isExternal = computed(() => isExternalClient())
    // "All tasks" includes completed/archived work (so it can be reviewed and
    // restored); the default board shows only active (open/in-progress) tasks.
    const statusOpts = computed(() => [
      { value: '',            label: t('common.all') },
      { value: 'open',        label: t('status.open') },
      { value: 'in_progress', label: t('status.in_progress') },
      { value: 'all',         label: t('tasks.allTasks') },
    ])
    const tasks    = ref([])
    const projects = ref([])
    const users    = ref([])
    const allFields  = ref([])   // every field the user can see (project + global)
    const fieldModalProject = ref(null)  // { id, name } when the modal is open

    async function loadFields() {
      if (isExternal.value) return
      try { allFields.value = (await api.getProjectFields()).data || [] } catch {}
    }
    // Fields visible for a given project id: its own + global, in sort order.
    function fieldsFor(projectId) {
      return allFields.value
        .filter(f => f.project_id === null || f.project_id === projectId)
        .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    }

    // The reorderable middle columns (besides the pinned subject + action).
    const BUILTIN_COLS = ['time', 'urgency', 'assignee']
    const isBuiltinCol = (key) => BUILTIN_COLS.includes(key)
    // Delete a custom field column (built-ins have no delete button). Removes the
    // field and its values everywhere it shows.
    async function deleteField(fieldId) {
      const f = allFields.value.find(x => x.id === fieldId)
      if (!confirm(t('fields.removeConfirm', { label: f ? f.label : '' }))) return
      try { await api.deleteProjectField(fieldId); await loadFields() }
      catch { toast.error(t('toast.actionFailed')) }
    }
    // A group's middle columns in display order: built-ins + its custom fields,
    // honoring the project's saved column_order, appending anything new and
    // dropping deleted field ids.
    function orderedMiddles(group) {
      const fieldIds = (group.fields || []).map(f => f.id)
      const stored = Array.isArray(group.column_order) ? group.column_order : []
      const order = stored.filter(k => BUILTIN_COLS.includes(k) || fieldIds.includes(k))
      for (const b of BUILTIN_COLS) if (!order.includes(b)) order.push(b)
      for (const id of fieldIds) if (!order.includes(id)) order.push(id)
      return order
    }
    function colLabel(group, key) {
      if (key === 'time')     return t('teamReports.timeCol')
      if (key === 'urgency')  return t('taskForm.urgency')
      if (key === 'assignee') return t('export.assignee')
      const f = (group.fields || []).find(x => x.id === key)
      return f ? f.label : ''
    }
    const colWidth = (key) =>
      key === 'time' ? '150px' : key === 'urgency' ? '116px' : key === 'assignee' ? '92px' : '130px'

    const loading  = ref(true)
    const showForm = ref(false)
    const drawerTask = ref(null)

    const isMine = computed(() => !!route.query.mine)

    // Group tasks under their project; tasks with no project fall into a final group.
    const groupedTasks = computed(() => {
      const groups = new Map()

      // Seed a group for every visible project so a project with no open tasks still
      // shows up (with its inline quick-add) — giving a place to add the first task.
      // Skip in the "mine" and search views, where empty projects would be noise; and
      // honour an active single-project filter.
      if (!isMine.value && !filters.q) {
        for (const p of projects.value) {
          if (filters.project_id && p.id !== Number(filters.project_id)) continue
          groups.set(p.id, {
            key: p.id,
            name: p.name,
            color: p.color,
            order: 0,
            tasks: [],
            fields: fieldsFor(p.id),
            column_order: Array.isArray(p.column_order) ? p.column_order : [],
          })
        }
      }

      for (const tk of tasks.value) {
        // Completed/archived tasks leave the active board (they're reachable again
        // via the "All tasks" filter). Filtering here means a completed task stays
        // gone even after a realtime refetch brings it back in the raw list.
        if (tk.archived_at && filters.status !== 'all') continue
        const key = tk.project_id || 0
        if (!groups.has(key)) {
          const proj = projects.value.find(p => p.id === key)
          groups.set(key, {
            key,
            name: tk.project_name || t('tasks.noProjectGroup'),
            color: tk.project_color,
            order: tk.project_id ? 0 : 1,
            tasks: [],
            fields: key ? fieldsFor(key) : [],
            column_order: proj && Array.isArray(proj.column_order) ? proj.column_order : [],
          })
        }
        groups.get(key).tasks.push(tk)
      }
      return [...groups.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
    })

    const filters = reactive({ status: '', urgency: '', project_id: route.query.project_id || '', q: '', due_from: '', due_to: '' })

    // silent=true skips the loading spinner so a realtime refresh doesn't unmount
    // the task rows (which would collapse any open subtask/comment panels).
    async function load(silent = false) {
      if (!silent) loading.value = true
      try {
        const params = {
          ...(isMine.value         ? { mine: 1 }                           : {}),
          // "all" widens the scope to include archived/completed tasks; the
          // open/in_progress tabs filter by status on the active set.
          ...(filters.status === 'all'
            ? { scope: 'all' }
            : (filters.status ? { status: filters.status } : {})),
          ...(filters.urgency      ? { urgency: filters.urgency }          : {}),
          ...(filters.project_id   ? { project_id: filters.project_id }    : {}),
          ...(filters.q.trim()     ? { q: filters.q.trim() }               : {}),
          ...(filters.due_from     ? { due_from: filters.due_from }        : {}),
          ...(filters.due_to       ? { due_to: filters.due_to }            : {}),
        }
        const res    = await api.getTasks(params)
        tasks.value  = res.data || []
      } catch {} finally {
        loading.value = false
      }
    }

    async function loadProjects() {
      try { projects.value = (await api.getProjects()).data || [] } catch {}
    }
    async function loadUsers() {
      if (isExternal.value) return
      try { users.value = (await api.getUsers()).data || [] } catch {}
    }

    // Inline quick-assign from a task card — no drawer needed.
    async function assignTask({ task, userIds }) {
      const prev = { assignees: task.assignees, assigned_to: task.assigned_to, assignee_name: task.assignee_name }
      const list = userIds.map(id => ({ id, name: users.value.find(u => u.id === id)?.name || null }))
      task.assignees     = list
      task.assigned_to   = list[0]?.id ?? null
      task.assignee_name = list[0]?.name ?? null
      try {
        // Trust the server's authoritative assignee set rather than the optimistic
        // one — if the backend dropped an id (e.g. not an active office member) the
        // UI must reflect what actually saved, not claim success falsely.
        const res    = await api.updateTask(task.id, { assignee_ids: userIds })
        const saved  = res.data || {}
        const serverAssignees = saved.assignees || []
        task.assignees     = serverAssignees
        task.assigned_to   = saved.assigned_to ?? null
        task.assignee_name = saved.assignee_name ?? null

        const savedIds  = serverAssignees.map(a => a.id).sort()
        const wantedIds = [...new Set(userIds)].sort()
        const stuck     = savedIds.length === wantedIds.length && savedIds.every((id, i) => id === wantedIds[i])
        if (!stuck) {
          toast.error(t('toast.actionFailed'))
        } else {
          toast.success(serverAssignees.length ? t('toast.assigned', { name: serverAssignees.map(a => a.name).join(', ') }) : t('toast.unassigned'))
        }
      } catch {
        Object.assign(task, prev)
        await load(true)
        toast.error(t('toast.actionFailed'))
      }
    }

    function resetFilters() {
      Object.assign(filters, { status: '', urgency: '', project_id: '', q: '', due_from: '', due_to: '' })
      load()
    }

    // Completing a task archives it (it leaves the active board and is reachable
    // again via the "All tasks" filter). Optimistic: flag it archived locally so
    // the board's TransitionGroup animates the row out immediately — no full
    // reload flash — then archive server-side and revert if that fails.
    async function completeTask(task) {
      const prev = task.archived_at
      task.archived_at = new Date().toISOString()
      drawerTask.value = null
      try {
        await api.archiveTask(task.id)
        toast.success(t('toast.taskCompleted'))
      } catch {
        task.archived_at = prev
        toast.error(t('toast.actionFailed'))
      }
    }
    async function reopenTask(task) {
      try { await api.reopenTask(task.id); await load(); toast.success(t('toast.taskReopened')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    async function restoreTask(task) {
      try { await api.restoreTask(task.id); drawerTask.value = null; await load(); toast.success(t('toast.taskRestored')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    function openTask(task) { drawerTask.value = task }
    function onDrawerSaved() { drawerTask.value = null; load() }
    const createDefaults = ref({})
    function openCreateForm() { createDefaults.value = {}; showForm.value = true }
    function openCreateInProject(pid) { createDefaults.value = { project_id: pid }; showForm.value = true }
    function closeForm() { showForm.value = false; createDefaults.value = {} }
    function onSaved() { load() }

    // ── Inline quick-add at the bottom of a project group (one open at a time) ──
    const quickAddPid  = ref(null)
    const quickAddText = ref('')
    async function openQuickAdd(g) {
      quickAddPid.value = g.key
      quickAddText.value = ''
      await nextTick()
      document.querySelector('.qa-input')?.focus()
    }
    function cancelQuickAdd() {
      quickAddPid.value = null
      quickAddText.value = ''
    }
    async function submitQuickAdd(g) {
      const title = quickAddText.value.trim()
      if (!title) return
      try {
        await api.createTask({ title, project_id: g.key })
        quickAddText.value = ''
        await load(true)
        // keep the field open + focused for rapid entry
        await nextTick()
        document.querySelector('.qa-input')?.focus()
      } catch { toast.error(t('toast.saveFailed')) }
    }

    // Grid template per group, in the project's column order: flexible subject with a
    // readable floor (custom fields must never crush the task title), each middle
    // column's width in order, then the action column. The header grid and every row
    // grid read this same --tt-cols, so with no column gap they align exactly. When the
    // total exceeds the card, .pt-scroll scrolls horizontally instead of clipping.
    function colsFor(group) {
      const widths = orderedMiddles(group).map(colWidth).join(' ')
      return `minmax(220px,1fr)${widths ? ' ' + widths : ''} 138px`
    }

    function exportTasks() {
      const rows = tasks.value.map(tk => ({
        [t('export.task')]:     tk.title,
        [t('export.project')]:  tk.project_name || '',
        [t('export.assignee')]: tk.assignee_name || '',
        [t('export.status')]:   t('status.' + tk.status),
        [t('export.urgency')]:  t('urgency.' + tk.urgency),
        [t('export.hours')]:    secondsToHours(tk.total_seconds),
        [t('export.due')]:      tk.due_at ? tk.due_at.slice(0, 10) : '',
      }))
      const stamp = new Date().toISOString().slice(0, 10)
      exportCsv(rows, `devtask-tasks-${stamp}.csv`)
    }

    function onTimerUpdate(payload) {
      const t = tasks.value.find(t => t.id === payload.taskId)
      if (t) {
        t.timer_seconds = payload.timer_seconds
        t.timer_started_at = payload.timer_started_at
        if (payload.status) t.status = payload.status
      }
    }

    // Hard delete (confirmed inside TaskCard). Optimistic removal; realtime
    // 'task.changed' confirms for everyone else.
    async function deleteTask(task) {
      try {
        await api.deleteTask(task.id)
        tasks.value = tasks.value.filter(x => x.id !== task.id)
      } catch { toast.error(t('toast.actionFailed')) }
    }

    function openFields(group) { fieldModalProject.value = { id: group.key, name: group.name } }

    // Optimistic inline value save.
    async function onFieldValue({ task, fieldId, value }) {
      const prev = { ...(task.field_values || {}) }
      const next = { ...(task.field_values || {}) }
      if (value === '' || value == null) delete next[fieldId]; else next[fieldId] = value
      task.field_values = next
      try { await api.updateTask(task.id, { field_values: { [fieldId]: value } }) }
      catch { task.field_values = prev; toast.error(t('toast.saveFailed')) }
    }

    // Drag-reorder the middle columns (built-ins + custom fields) within a group.
    // The new order is saved on the project and applied optimistically so the grid
    // reflows immediately.
    const drag = reactive({ projectId: null, from: -1, over: -1 })
    function dragStart(group, i) { drag.projectId = group.key; drag.from = i; drag.over = i }
    function dragEnter(group, i) { if (drag.projectId === group.key) drag.over = i }
    function dragEnd() { drag.projectId = null; drag.from = -1; drag.over = -1 }
    // Colour the column being dragged and the one it would drop onto.
    function colClass(group, i) {
      if (drag.projectId !== group.key) return {}
      return { dragging: drag.from === i, 'drag-over': drag.over === i && drag.from !== i }
    }
    async function dropOn(group, i) {
      if (drag.projectId !== group.key || drag.from < 0 || drag.from === i) { drag.over = -1; return }
      const order = orderedMiddles(group)
      const [moved] = order.splice(drag.from, 1)
      order.splice(i, 0, moved)
      drag.from = -1; drag.over = -1
      const proj = projects.value.find(p => p.id === group.key)
      const prev = proj ? proj.column_order : null
      if (proj) proj.column_order = order
      try { await api.reorderProjectColumns(group.key, order) }
      catch { if (proj) proj.column_order = prev; toast.error(t('toast.actionFailed')) }
    }

    // React to navigation between project/mine views (e.g. clicking "View tasks").
    watch(() => [route.query.mine, route.query.project_id], () => {
      filters.project_id = route.query.project_id || ''
      load()
    })
    let offRealtime = () => {}
    let offFields = () => {}
    onMounted(() => {
      load(); loadProjects(); loadUsers(); loadFields()
      offRealtime = onRealtime('task.changed', debounce(() => load(true), 400))
      offFields = onRealtime('project.changed', debounce(() => { loadFields(); loadProjects() }, 400))
    })
    onUnmounted(() => { offRealtime(); offFields() })

    return {
      statusOpts, tasks, groupedTasks, projects, users, loading, showForm, drawerTask, createDefaults,
      isMine, isExternal, filters, t,
      load, resetFilters, completeTask, reopenTask, restoreTask, assignTask, deleteTask,
      openTask, onDrawerSaved, openCreateForm, openCreateInProject, closeForm, onSaved, onTimerUpdate, exportTasks,
      quickAddPid, quickAddText, openQuickAdd, cancelQuickAdd, submitQuickAdd,
      colsFor, allFields, fieldsFor, orderedMiddles, colLabel, isBuiltinCol, deleteField, fieldModalProject, openFields, onFieldValue,
      dragStart, dragEnter, dragEnd, colClass, dropOn,
      editProject,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.header-actions { display: flex; gap: 8px; align-items: center; }

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 14px 16px;
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: var(--radius);
  box-shadow: 0 2px 10px rgba(20,24,40,.05);
}
.status-tabs { display: flex; gap: 4px; }
.tab-btn {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: none;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
}
.tab-btn:hover  { border-color: var(--primary); color: var(--primary); }
.tab-btn.active { background: var(--primary); border-color: var(--primary); color: #fff; }

.filter-controls { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
.filter-sel    { width: 140px; padding: 6px 10px; font-size: 12px; }
.filter-search { width: 180px; padding: 6px 10px; font-size: 12px; }
.date-filter   { display: flex; align-items: center; gap: 6px; }
.filter-date   { width: 140px; padding: 6px 10px; font-size: 12px; }
.date-sep      { color: var(--muted); }

.tasks-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Project groups rendered as table cards. */
.task-groups { display: flex; flex-direction: column; gap: 22px; }
.proj-table {
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(20,24,40,.07);
}

/* Project box header */
.pt-head { display: flex; align-items: center; gap: 11px; padding: 14px 18px; }
.pt-dot { width: 11px; height: 11px; border-radius: 4px; flex-shrink: 0; }
.pt-name { font-size: var(--fs-h4); font-weight: 800; letter-spacing: -.01em; }
.pt-count {
  min-width: 22px; height: 22px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--danger); color: #fff;
  border-radius: 999px; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums;
}
/* The action cluster ("+ New Task", "+ Add field", 3-dot) is pushed to the far
   (left) end of the header via an auto-margin on the first action; the title +
   count stay pinned to the start (right) edge. */
.pt-add {
  margin-inline-start: auto;
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--accent); color: var(--on-accent);
  border: none; border-radius: 20px; padding: 7px 14px;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.pt-add:hover { filter: brightness(1.05); }
.pt-field { background: var(--surface-2); color: var(--accent-ink); border: 1px dashed color-mix(in srgb, var(--accent-ink) 40%, transparent); }
.pt-field:hover { filter: none; background: var(--accent-wash); }
.pt-menu {
  border: 1px solid var(--border-2); background: var(--surface);
  color: var(--muted); border-radius: 8px;
  width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center;
  padding: 0; flex-shrink: 0; cursor: pointer;
}
.pt-menu:hover { color: var(--text); background: var(--hover); border-color: var(--accent); }

/* Inline quick-add row at the bottom of a project's task list. */
.pt-quickadd { padding: 8px 14px 12px; }
.qa-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  background: none; border: none; border-radius: var(--radius-sm);
  color: var(--accent-ink); font-size: 13px; font-weight: 600; cursor: pointer;
}
.qa-btn:hover { background: var(--accent-wash); }
.qa-btn svg { color: var(--accent); }
.qa-input {
  width: 100%; padding: 8px 11px;
  background: var(--surface-2); border: 1px solid transparent;
  box-shadow: inset 0 0 0 1px var(--accent);
  border-radius: var(--radius-sm); color: var(--text); font-size: 13px; outline: none;
}

/* Completing a task fades + slides its row out, and the rows below glide up to
   fill the gap — a soft transition instead of a hard reload flash. */
.pt-rows { position: relative; }
.trow-leave-active {
  transition: opacity .3s ease, transform .3s ease;
  position: absolute; left: 0; right: 0; z-index: 0;
}
.trow-leave-to { opacity: 0; transform: translateX(-24px); }
.trow-move { transition: transform .3s ease; }
.ptc-field { cursor: grab; user-select: none; border-radius: 6px; transition: background .12s, color .12s, box-shadow .12s, opacity .12s;
  display: inline-flex; align-items: center; gap: 3px; }
.ptc-field:active { cursor: grabbing; }
.ptc-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
/* Delete a custom-field column — reveals on hover, red on hover. A real click
   target (min ~24px square) so it's easy to hit, not a tiny 16px glyph. */
.ptc-del {
  flex-shrink: 0; border: none; background: none; cursor: pointer;
  color: var(--faint); font-size: 17px; line-height: 1;
  width: 24px; height: 24px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 6px; opacity: 0; transition: opacity .12s, color .12s, background .12s;
}
.ptc-field:hover .ptc-del { opacity: 1; }
.ptc-del:hover { color: var(--danger); background: var(--st-red-bg); }
/* Drag feedback: the column being dragged dims with an accent tint; the column it
   would drop onto is highlighted with an accent edge. */
.ptc-field.dragging  { opacity: .5; background: var(--accent-wash); color: var(--accent-ink); }
.ptc-field.drag-over { background: var(--accent-wash); color: var(--accent-ink); box-shadow: inset 0 -3px 0 var(--accent); }

/* Shared horizontal scroller: header + rows scroll together when a project's
   column set is wider than the card — the subject column never gets crushed. */
.pt-scroll { overflow-x: auto; }
.pt-scroll > .pt-cols,
.pt-scroll > .pt-rows { min-width: min-content; }

/* Column headers — aligned to the same grid as the rows. */
.pt-cols {
  display: grid; grid-template-columns: var(--tt-cols);
  background: var(--surface-2);
  border-block: 1px solid var(--row-line);
}
.ptc {
  padding: 8px 14px; font-size: 13px; font-weight: 700;
  color: var(--text); text-transform: uppercase; letter-spacing: .03em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
/* The table rows are TaskCards; drop the trailing divider on the last one. */
.proj-table > .task-card-wrap:last-child :deep(.task-row.tbl) { border-bottom: none; }

@media (max-width: 760px) {
  .pt-cols { display: none; }
}

@media (max-width: 700px) {
  .filter-controls { flex-direction: column; }
  .filter-sel, .filter-search { width: 100%; }
  .tasks-grid { grid-template-columns: 1fr; }
}
</style>
